/**
 * One intro page per audience: /start/fire-official, /start/scout, and so on.
 *
 * Glenn's Sept 22 2026 email asked for "unique access points ... so that what
 * they see is clear and simple for that audience". The two insurer links
 * (/?property=&model=) proved the idea; they just land on the map and leave
 * the visitor to work out why they are there. This page says it first.
 *
 * Public: listed in the signed-out whitelist in app/_layout.tsx. Every word
 * on screen comes from src/constants/audiences.ts, so the client's copy edits
 * touch one file and no layout.
 */
import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import Head from 'expo-router/head';
import { router, useLocalSearchParams, type Href } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, SPACING, TYPOGRAPHY, BORDER_RADIUS, NARROW_MAX_WIDTH } from '../../src/theme';
import { BrandTile, Button, RediLogo, TextLink } from '@components/common';
import { useFieldMode } from '@hooks/useFieldMode';
import { useUIStore } from '../../src/store/uiStore';
import { useAuthStore } from '../../src/store/authStore';
import { useBrandStore, brandFromChecklist } from '../../src/store/brandStore';
import { useChecklistStore } from '../../src/store/checklistStore';
import { insurerPageBySlug, INSURER_POINT_TITLES } from '../../src/constants/insurerPages';
import {
  AUDIENCES,
  DEFAULT_AUDIENCE,
  audienceBySlug,
  type Audience,
} from '../../src/constants/audiences';
import { USER_ROLE_META } from '../../src/types';

export default function AudienceIntroScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const {
    audience: slug,
    property,
    model,
  } = useLocalSearchParams<{
    audience?: string;
    property?: string;
    model?: string;
  }>();
  const user = useAuthStore((s) => s.user);
  const setPendingAudience = useUIStore((s) => s.setPendingAudience);
  const setPendingProperty = useUIStore((s) => s.setPendingProperty);
  const requestMapPathsOverlay = useUIStore((s) => s.requestMapPathsOverlay);
  const fieldMode = useFieldMode();

  // An insurer slug (/start/fair-plan) is checked first: it works for any
  // homeowner, with no property in the link.
  const insurerPage = insurerPageBySlug(slug);
  const setPendingModel = useUIStore((s) => s.setPendingModel);
  const requestMapSearchFocus = useUIStore((s) => s.requestMapSearchFocus);
  const markMapPathsEngaged = useUIStore((s) => s.markMapPathsEngaged);
  const resolvePreviewChecklist = useChecklistStore((s) => s.resolvePreviewChecklist);
  const setActiveBrand = useBrandStore((s) => s.setActiveBrand);
  const modelId = insurerPage?.modelId;
  // True once the insurer's own checklist has loaded and its brand is the
  // active one. Until then (or if the model is missing) the page renders
  // without a mark rather than showing another insurer's.
  const [brandReady, setBrandReady] = useState(false);

  // An unknown slug falls back to Fire Scout rather than +not-found. These
  // links are typed by hand into email; a mistyped one in front of a fire
  // chief should land on something that reads as REDI. Deliberately unlike
  // the "a mistyped share link should say so" rule in app/_layout.tsx.
  const audience: Audience = insurerPage
    ? AUDIENCES['property-owner']!
    : (audienceBySlug(slug) ?? AUDIENCES[DEFAULT_AUDIENCE]!);

  // Carry the audience across the sign-up round trip (sessionStorage-mirrored
  // on web), so the role picker opens with this role already ticked.
  useEffect(() => {
    setPendingAudience(audience.id);
  }, [audience.id, setPendingAudience]);

  useEffect(() => {
    if (!modelId) return;
    setPendingModel(modelId);
    let cancelled = false;
    setBrandReady(false);
    resolvePreviewChecklist(modelId)
      .then((checklist) => {
        if (cancelled || checklist?.id !== modelId) return;
        const brand = brandFromChecklist(checklist);
        if (!brand) return;
        setActiveBrand(brand);
        setBrandReady(true);
      })
      .catch(() => {
        // Offline or missing model: the page stands without the mark.
      });
    return () => {
      cancelled = true;
    };
  }, [modelId, setPendingModel, resolvePreviewChecklist, setActiveBrand]);

  // A property-owner link can still carry the insurer's deep-link params.
  // Prime the same store the map's usePropertyLinkIntake would have, so the
  // page can sit in front of the existing insurer links without a second
  // mechanism.
  useEffect(() => {
    if (!property) return;
    setPendingProperty({ structureId: property, checklistId: model || undefined });
  }, [property, model, setPendingProperty]);

  const handleCta = () => {
    if (insurerPage) {
      // To the map with the address bar focused; the map screen consumes the
      // one-shot flag (same path as the Property tab with no home yet).
      // "Find your home" is the welcome overlay's Property choice already
      // made, so mark it engaged: otherwise a first visit lands behind the
      // overlay, which hides the search bar.
      markMapPathsEngaged();
      requestMapSearchFocus();
      router.push('/(tabs)' as Href);
      return;
    }
    // Field mode (phone website) has no Guidance overlay to summon.
    if (audience.cta.showMapPaths && !fieldMode) requestMapPathsOverlay();
    router.push(audience.cta.href);
  };

  const handleSignUp = () => {
    router.push('/(auth)/sign-up' as Href);
  };

  const view = insurerPage
    ? {
        eyebrow: insurerPage.eyebrow,
        headline: insurerPage.headline,
        lede: insurerPage.lede,
        ctaLabel: insurerPage.cta.label,
        points: audience.points.map((p, i) => ({
          ...p,
          title: INSURER_POINT_TITLES[i] ?? p.title,
          body: insurerPage.points[i] ?? p.body,
        })),
      }
    : {
        eyebrow: audience.eyebrow,
        headline: audience.headline,
        lede: audience.lede,
        ctaLabel: audience.cta.label,
        points: audience.points,
      };

  return (
    <View style={[styles.screen, { backgroundColor: colors.background }]}>
      <Head>
        <title>{`${view.headline} | REDI`}</title>
        <meta name="description" content={view.lede} />
        {insurerPage ? <meta name="robots" content="noindex" /> : null}
      </Head>
      <ScrollView
        contentContainerStyle={[
          styles.content,
          { paddingTop: insets.top + SPACING.xl, paddingBottom: insets.bottom + SPACING.xxl },
        ]}
        testID="audience-intro-scroll">
        <View
          style={styles.inner}
          testID={insurerPage ? 'insurer-page' : `audience-intro-${audience.id}`}>
          <RediLogo fontSize={26} />
          {insurerPage && brandReady ? (
            <View style={styles.brand} testID="insurer-page-brand">
              <BrandTile variant="welcome" />
            </View>
          ) : null}

          {/* textSecondary, not textTertiary: colors.ts reserves tertiary for
              hints, placeholders and disabled controls because it is sub-AA,
              and none of the three small lines on this page is one of those.
              The eyebrow is how a reader knows the page is addressed to them. */}
          <Text style={[styles.eyebrow, { color: colors.textSecondary }]}>{view.eyebrow}</Text>
          <Text style={[styles.headline, { color: colors.text }]}>{view.headline}</Text>
          <Text style={[styles.lede, { color: colors.textSecondary }]}>{view.lede}</Text>

          <View style={styles.points}>
            {view.points.map((point) => {
              const Icon = point.icon;
              return (
                <View key={point.title} style={styles.point}>
                  <View style={[styles.pointIcon, { backgroundColor: colors.surfaceAlt }]}>
                    <Icon size={18} color={colors.text} strokeWidth={1.75} />
                  </View>
                  <View style={styles.pointText}>
                    <Text style={[styles.pointTitle, { color: colors.text }]}>{point.title}</Text>
                    <Text style={[styles.pointBody, { color: colors.textSecondary }]}>
                      {point.body}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>

          <Button
            title={view.ctaLabel}
            onPress={handleCta}
            fullWidth
            size="lg"
            testID="audience-intro-cta"
          />

          {!user && (
            <View style={styles.signUp}>
              <TextLink
                label="Create an account"
                onPress={handleSignUp}
                testID="audience-intro-signup"
              />
              <Text style={[styles.signUpNote, { color: colors.textSecondary }]}>
                {audience.signUpNote}
              </Text>
            </View>
          )}

          {/* The role blurb promises lower premiums, which on an insurer's
              own page reads as that insurer's promise. Audience pages only. */}
          {!insurerPage && (
            <Text style={[styles.roleNote, { color: colors.textSecondary }]}>
              {USER_ROLE_META[audience.id].blurb}
            </Text>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  content: {
    flexGrow: 1,
    paddingHorizontal: SPACING.lg,
    alignItems: 'center',
  },
  inner: {
    width: '100%',
    maxWidth: NARROW_MAX_WIDTH,
  },
  brand: { marginTop: SPACING.lg },
  eyebrow: {
    ...TYPOGRAPHY.overline,
    marginTop: SPACING.xxl,
  },
  headline: {
    ...TYPOGRAPHY.h1,
    marginTop: SPACING.sm,
  },
  lede: {
    ...TYPOGRAPHY.body,
    marginTop: SPACING.md,
  },
  points: {
    marginTop: SPACING.xl,
    marginBottom: SPACING.xl,
    gap: SPACING.lg,
  },
  point: {
    flexDirection: 'row',
    gap: SPACING.md,
  },
  pointIcon: {
    width: 36,
    height: 36,
    borderRadius: BORDER_RADIUS.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pointText: { flex: 1 },
  pointTitle: TYPOGRAPHY.h4,
  pointBody: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xxs,
  },
  signUp: {
    marginTop: SPACING.lg,
    alignItems: 'center',
    gap: SPACING.xxs,
  },
  signUpNote: TYPOGRAPHY.caption,
  roleNote: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.xxl,
    textAlign: Platform.OS === 'web' ? 'center' : 'auto',
  },
});
